import { useContext, useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import { motion } from "framer-motion";

import {
  Settings,
  Calculator,
  Headphones,
  Users,
  FileText,
  Timer,
  Mic,
  Bot,
  ChevronLeft,
  ChevronRight,
  Star,
  Clock,
  AlertTriangle,
  Info,
  Zap,
  ArrowRight,
  Search,
  Shield,
  Megaphone,
  FileCheck,
  BookOpen,
  Brain,
  Calendar,
  Key,
  BarChart3,
  Eye,
  Sparkles,
  Filter,
  Grid3X3,
  List,
  TrendingUp,
  Play,
  Layers,
  Rocket
} from "lucide-react";

// Tools data and configuration
const iconMap = {
  "Access Management": Shield,
  "Agent Assist": Bot,
  "Announcements": Megaphone,
  "Coaching Transcriber": Headphones,
  "Gnosis Timer": Timer,
  "HRPay AI Assistant": Users,
  "IDP Monitoring": BookOpen,
  "MOP Assistant": Brain,
  "Resume Analysis": FileText,
  "MOM Generator": Calendar,
  "Password Manager": Key,
  "QA Audio Transcriber": BarChart3,
  "Rebate Calculator": Calculator,
  "Rebate Viewer": Eye,
  "AI Arena": Sparkles,
  "Sentiment Analysis": TrendingUp,
  "Survey Tools": FileCheck
};

const categoryColors = {
  AI: {
    gradient: "from-purple-500 via-violet-500 to-purple-600",
    bg: "bg-purple-500/10",
    border: "border-purple-500/20",
    text: "text-purple-600",
    icon: "🤖",
    oldGradient: "from-purple-500 to-pink-500",
  },
  Transcription: {
    gradient: "from-blue-500 via-cyan-500 to-blue-600",
    bg: "bg-blue-500/10",
    border: "border-blue-500/20",
    text: "text-blue-600",
    icon: "🎙️",
    oldGradient: "from-blue-500 to-cyan-500",
  },
  Admin: {
    gradient: "from-green-500 via-emerald-500 to-green-600",
    bg: "bg-green-500/10",
    border: "border-green-500/20",
    text: "text-green-600",
    icon: "⚙️",
    oldGradient: "from-green-500 to-emerald-500",
  },
  // Monitoring category follows UAS brand yellow
  Monitoring: {
    gradient: "from-uasYellow via-uasYellow to-uasYellow",
    bg: "bg-uasYellow/10",
    border: "border-uasYellow/20",
    text: "text-uasYellow",
    icon: "📊",
    oldGradient: "from-uasYellow to-uasYellow",
  },
  Tools: {
    gradient: "from-indigo-500 via-purple-500 to-indigo-600",
    bg: "bg-indigo-500/10",
    border: "border-indigo-500/20",
    text: "text-indigo-600",
    icon: "🛠️",
    oldGradient: "from-indigo-500 to-purple-500",
  },
};

const allTools = [
  {
    name: "Access Management",
    path: "/access-management",
    description: "Controls user accounts (create, deactivate, reset passwords).",
    category: "Admin",
    whoUses: "Supervisors, Managers",
    howToUse: "Log in → Click Access Management → Use Add/Edit/Delete/Reset.",
    commonErrors: [
      "❗ Can't find user? Use filters by supervisor or department.",
      "❗ Reset not working? Ensure OTP is used within 10 minutes.",
    ],
    bestPractices: [
      "Regularly audit inactive accounts.",
      "Always assign least-privilege roles.",
    ],
    popularity: 95,
    lastUpdated: "2 days ago",
    isNew: false,
    isTrending: true,
  },
  {
    name: "Agent Assist",
    path: "/agent-assist",
    description: "Provides AI-powered support answers.",
    category: "AI",
    whoUses: "Frontline agents",
    howToUse: "Type a query → Wait for response → Copy/Save for ticket use.",
    commonErrors: ["❗ Vague answers? Rephrase your query."],
    bestPractices: [
      "Use for FAQs, not unique edge cases.",
      "Document useful answers in internal wikis.",
    ],
    popularity: 89,
    lastUpdated: "1 day ago",
    isNew: false,
    isTrending: true,
  },
  {
    name: "Announcements",
    path: "/admin/announcements",
    description: "Creates internal notices (e.g., updates, memos).",
    category: "Admin",
    whoUses: "Admins, Team Managers",
    howToUse: "Click Announcements → + New Announcement → Fill out & publish.",
    commonErrors: [
      "❗ Not showing to users? Check 'Target Audience' selection.",
    ],
    bestPractices: [
      "Use 'Priority 1' for time-sensitive content.",
      "Avoid jargon in company-wide announcements.",
    ],
    popularity: 78,
    lastUpdated: "3 days ago",
    isNew: false,
    isTrending: false,
  },
  {
    name: "Coaching Transcriber",
    path: "/coaching-transcriber",
    description: "Analyzes coaching recordings to auto-generate reports.",
    category: "Transcription",
    whoUses: "Coaches, Team Leads",
    howToUse: "Upload MP3/WAV → Click Analyze → Review generated report.",
    commonErrors: [
      "❗ Poor transcript? Ensure audio clarity; avoid background noise.",
    ],
    bestPractices: [
      "Review 'Root Cause' & 'Action Plan' with the agent post-session.",
    ],
    popularity: 91,
    lastUpdated: "4 hours ago",
    isNew: true,
    isTrending: true,
  },
  {
    name: "Gnosis Timer",
    path: "/gnosis",
    description: "Manages multiple active/paused task timers.",
    category: "Tools",
    whoUses: "Support agents handling multiple issues",
    howToUse: "Click Gnosis → Set timer → Pause/Resume as needed.",
    commonErrors: [
      "❗ Timer didn't alert? Ensure tab is active or notifications are enabled.",
    ],
    bestPractices: ["Use max of 4 timers to avoid multitasking fatigue."],
    popularity: 76,
    lastUpdated: "2 days ago",
    isNew: false,
    isTrending: false,
  },
  {
    name: "HRPay AI Assistant",
    path: "/hrpay",
    description: "Answers HR and payroll-related questions via chatbot.",
    category: "AI",
    whoUses: "All employees",
    howToUse: "Ask question → Rate response → Escalate if unsatisfied.",
    commonErrors: [
      "❗ Didn't answer correctly? Escalate to support ticket.",
    ],
    bestPractices: [
      "Use for routine HR questions (leave filing, payslip issues).",
    ],
    popularity: 87,
    lastUpdated: "6 hours ago",
    isNew: false,
    isTrending: true,
  },
  {
    name: "IDP Monitoring",
    path: "/idp-monitoring",
    description: "Tracks personal and team learning goals.",
    category: "Monitoring",
    whoUses: "All staff, especially HR and Managers",
    howToUse:
      "Click IDP Monitoring → View/Edit Progress → Add training if authorized.",
    commonErrors: [
      "❗ Locked out of training updates? You may lack permission.",
    ],
    bestPractices: ["Update percentage monthly; link to coaching feedback."],
    popularity: 73,
    lastUpdated: "1 day ago",
    isNew: false,
    isTrending: false,
  },
  {
    name: "MOP Assistant",
    path: "/webview",
    description: "Creates, analyzes, or improves Method of Procedures via AI.",
    category: "AI",
    whoUses: "Ops, Engineering, Documentation teams",
    howToUse:
      "Upload or request new MOP → Receive AI draft → Export.",
    commonErrors: [
      "❗ Bad formatting? Rephrase prompt or manually polish.",
    ],
    bestPractices: ["Always manually review for compliance."],
    popularity: 93,
    lastUpdated: "3 hours ago",
    isNew: true,
    isTrending: true,
  },
  {
    name: "Resume Analysis",
    path: "/resume-analysis",
    description: "Matches resumes against job descriptions.",
    category: "AI",
    whoUses: "TA/Recruitment",
    howToUse: "Upload resume → Enter job description → Click Analyze.",
    commonErrors: [
      "❗ Low fit score? Check resume file format or completeness.",
    ],
    bestPractices: [
      "Use 'Generate Interview Questions' to streamline screening.",
    ],
    popularity: 85,
    lastUpdated: "1 day ago",
    isNew: false,
    isTrending: false,
  },
  {
    name: "MOM Generator",
    path: "/audio-transcriber",
    description: "Transcribes meetings into MoM summaries.",
    category: "Transcription",
    whoUses: "Admins, Secretaries, Project Leads",
    howToUse: "Upload meeting audio → Review summary → Export.",
    commonErrors: [
      "❗ Transcription error? Use clearer recordings or post-edit.",
    ],
    bestPractices: [
      "Always include action items and responsible persons.",
    ],
    popularity: 88,
    lastUpdated: "5 hours ago",
    isNew: false,
    isTrending: true,
  },
  {
    name: "Password Manager",
    path: "/password-manager",
    description: "Stores and manages system credentials securely.",
    category: "Admin",
    whoUses: "All team members with credentials",
    howToUse: "Add New → Fill Service, Username, Password → Save.",
    commonErrors: ["❗ Can't find entry? Use Refresh button."],
    bestPractices: [
      "Don't store personal passwords. Use org accounts only.",
    ],
    popularity: 71,
    lastUpdated: "2 days ago",
    isNew: false,
    isTrending: false,
  },
  {
    name: "QA Audio Transcriber",
    path: "/qa-audio-transcriber",
    description:
      "Transcribes and scores call recordings for quality.",
    category: "Transcription",
    whoUses: "QA Analysts",
    howToUse:
      "Upload call → Analyze → Review QA scoring categories.",
    commonErrors: [
      "❗ Missing sections? Re-check file type or clarity.",
    ],
    bestPractices: [
      "Use reports for coaching, not just compliance.",
    ],
    popularity: 79,
    lastUpdated: "8 hours ago",
    isNew: false,
    isTrending: false,
  },
  {
    name: "Rebate Calculator",
    path: "/rebate-calculator",
    description:
      "Calculates billing rebates due to service issues.",
    category: "Tools",
    whoUses: "Agents",
    howToUse:
      "Enter billing cycle → Input days affected → Submit CARE Summary.",
    commonErrors: [
      "❗ Duplicate ticket number? Check for previous entries.",
    ],
    bestPractices: [
      "Always include RFO (root cause) with resolution notes.",
    ],
    popularity: 94,
    lastUpdated: "2 hours ago",
    isNew: false,
    isTrending: true,
  },
  {
    name: "Rebate Viewer",
    path: "/rebate-viewer",
    description:
      "Tracks, filters, and approves rebate submissions.",
    category: "Tools",
    whoUses: "Supervisors, QA, Billing Managers",
    howToUse:
      "Go to Rebate Viewer → Use filters → Approve/Reject entries.",
    commonErrors: [
      "❗ Not seeing team? Set filters by Team Leader or Line of Business.",
    ],
    bestPractices: [
      "Use dashboard pie chart to assess processing delays.",
    ],
    popularity: 90,
    lastUpdated: "1 hour ago",
    isNew: false,
    isTrending: true,
  },
  {
    name: "AI Arena",
    path: "/agent-assist-gpt4o",
    description:
      "Experiment with and compare AI prompts, models, and presets.",
    category: "AI",
    whoUses: "Developers, Analysts, Power Users",
    howToUse:
      "Open AI Arena → pick a model/preset → run side-by-side prompts → save the best.",
    commonErrors: [
      "❗ Rate limited? Reduce parallel runs or switch model.",
      "❗ Empty output? Check prompt variables and context length.",
    ],
    bestPractices: [
      "Version your prompts and save winners as presets.",
      "Use the same seed/settings for fair A/B comparisons.",
    ],
    popularity: 84,
    lastUpdated: "2 hours ago",
    isNew: true,
    isTrending: true,
  },
  {
    name: "Sentiment Analysis",
    path: "/audio-sentiment",
    description:
      "Score text/audio transcripts for sentiment and key emotion drivers.",
    category: "Monitoring",
    whoUses: "QA, Supervisors, CX Analysts",
    howToUse:
      "Upload text or transcript → Analyze → review sentiment timeline and factors.",
    commonErrors: [
      "❗ Low confidence? Provide longer context or clearer audio.",
      "❗ File rejected? Use supported formats (TXT, CSV, JSON, or WAV/MP3 transcript).",
    ],
    bestPractices: [
      "Tag outcomes (Resolved/Unresolved) to correlate with sentiment.",
      "Export CSV and trend by team/week for coaching.",
    ],
    popularity: 82,
    lastUpdated: "3 hours ago",
    isNew: false,
    isTrending: true,
  },
  {
    name: "Survey Tools",
    path: "/esatsurvey",
    description:
      "Build, distribute, and track employee/customer surveys.",
    category: "Tools",
    whoUses: "HR, Ops, Research",
    howToUse:
      "Create survey → choose audience → send link/QR → track completions & scores.",
    commonErrors: [
      "❗ Not receiving responses? Check audience filters and link expiry.",
      "❗ Missing fields? Validate question types and required flags.",
    ],
    bestPractices: [
      "Keep surveys under 10 questions to reduce drop-off.",
      "Use benchmarks and compare across periods.",
    ],
    popularity: 88,
    lastUpdated: "1 day ago",
    isNew: false,
    isTrending: true,
  },
];

const categories = ["All", "AI", "Transcription", "Admin", "Monitoring", "Tools"];

const HomePage = () => {
  const { user } = useContext(AuthContext);
  const [isLoaded, setIsLoaded] = useState(false);
  const [activityLogs, setActivityLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsError, setLogsError] = useState(null);
  const [lastFetch, setLastFetch] = useState(null);
  const [cacheInfo, setCacheInfo] = useState({ cached: false, stale: false });

  // Tools section states
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("All");
  const [hoveredTool, setHoveredTool] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [viewMode, setViewMode] = useState("grid");
  const [sortBy, setSortBy] = useState("popularity");

  // Announcement states
  const [announcements, setAnnouncements] = useState([]);
  const [announcementsLoading, setAnnouncementsLoading] = useState(false);
  const [announcementsError, setAnnouncementsError] = useState(null);
  const [lastAnnouncementFetch, setLastAnnouncementFetch] = useState(null);
  const [announcementCacheInfo, setAnnouncementCacheInfo] = useState({
    cached: false,
    stale: false,
  });

  // Carousel states
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const fetchingRef = useRef(false);
  const announcementFetchingRef = useRef(false);

  const navigate = useNavigate();
  const location = useLocation();

  const isOnHomepage =
    location.pathname === "/home" ||
    location.pathname === "/" ||
    location.pathname === "";

  // Tools filtering and sorting
  const filteredTools = allTools
    .filter(
      (tool) =>
        tool.name.toLowerCase().includes(searchTerm.toLowerCase()) &&
        (activeTab === "All" || tool.category === activeTab)
    )
    .sort((a, b) => {
      switch (sortBy) {
        case "popularity":
          return b.popularity - a.popularity;
        case "name":
          return a.name.localeCompare(b.name);
        case "category":
          return a.category.localeCompare(b.category);
        case "updated":
          return new Date(b.lastUpdated) - new Date(a.lastUpdated);
        default:
          return 0;
      }
    });

  const handleCardClick = (path) => {
    navigate(path);
  };

  const handleInfoHover = (tool, event) => {
    const rect = event.target.getBoundingClientRect();
    const tooltipWidth = 320;
    const tooltipHeight = 400;
    const gap = 10;

    let x = rect.right + gap;
    let y = rect.top;

    if (x + tooltipWidth > window.innerWidth) {
      x = rect.left - tooltipWidth - gap;
    }

    if (x < 0) {
      x = gap;
    }

    y = rect.top + rect.height / 2 - tooltipHeight / 2;

    if (y + tooltipHeight > window.innerHeight) {
      y = window.innerHeight - tooltipHeight - gap;
    }

    if (y < gap) {
      y = gap;
    }

    setTooltipPosition({ x, y });
    setHoveredTool(tool);
  };

  const handleInfoLeave = () => {
    setHoveredTool(null);
  };

  // Category stats
  const getCategoryStats = () => {
    const stats = {};
    categories.forEach((cat) => {
      if (cat === "All") {
        stats[cat] = allTools.length;
      } else {
        stats[cat] = allTools.filter((tool) => tool.category === cat).length;
      }
    });
    return stats;
  };

  const categoryStats = getCategoryStats();

  // Announcement fetch function (existing logic)
  const fetchAnnouncements = useCallback(
    async (forceRefresh = false) => {
      if (announcementFetchingRef.current) {
        return;
      }

      const now = Date.now();
      const cacheExpiry = 60 * 60 * 1000;

      if (
        !forceRefresh &&
        lastAnnouncementFetch &&
        now - lastAnnouncementFetch.getTime() < cacheExpiry
      ) {
        return;
      }

      const token = localStorage.getItem("token");
      if (!token) {
        return;
      }

      announcementFetchingRef.current = true;
      setAnnouncementsLoading(true);
      setAnnouncementsError(null);

      try {
        const res = await fetch("http://localhost:5000/api/public", {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          signal: AbortSignal.timeout(15000),
        });

        if (!res.ok) {
          const errorText = await res.text();
          throw new Error(`HTTP ${res.status}: ${res.statusText}`);
        }

        const data = await res.json();

        if (data.success) {
          const allAnnouncements = data.announcements || [];

          const activeAnnouncements = allAnnouncements.filter((announcement) => {
            if (!announcement.isActive) return false;

            const targetRoles = announcement.targetRoles || ["all"];
            const userRole = user?.role || "guest";
            const roleMatches =
              targetRoles.includes("all") || targetRoles.includes(userRole);

            return roleMatches;
          });

          setAnnouncements(activeAnnouncements);
          setLastAnnouncementFetch(new Date());

          setAnnouncementCacheInfo({
            cached: data.cached || false,
            stale: data.stale || false,
            cacheAge: data.cacheAge || 0,
          });
        } else {
          throw new Error(data.error || "Failed to fetch announcements");
        }
      } catch (err) {
        console.error("❌ Error fetching announcements:", err.message);

        if (err.name === "AbortError" || err.message.includes("timeout")) {
          setAnnouncementsError("Request timed out. Please try again.");
        } else if (err.message.includes("fetch")) {
          setAnnouncementsError("Network error. Please check your connection.");
        } else if (err.message.includes("401") || err.message.includes("403")) {
          setAnnouncementsError("Authentication required.");
        } else {
          setAnnouncementsError(err.message);
        }
      } finally {
        setAnnouncementsLoading(false);
        announcementFetchingRef.current = false;
      }
    },
    [lastAnnouncementFetch, user?.role]
  );

  // Logs fetch function (existing logic)
  const fetchLogs = useCallback(
    async (forceRefresh = false) => {
      if (fetchingRef.current) {
        return;
      }

      const now = Date.now();
      const cacheExpiry = 3 * 60 * 1000;

      if (!forceRefresh && lastFetch && now - lastFetch.getTime() < cacheExpiry) {
        return;
      }

      fetchingRef.current = true;
      setLogsLoading(true);
      setLogsError(null);

      try {
        const res = await fetch("http://localhost:5000/api/logs/fetch", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          signal: AbortSignal.timeout(15000),
        });

        if (!res.ok) {
          throw new Error(`HTTP ${res.status}: ${res.statusText}`);
        }

        const data = await res.json();

        if (data.success) {
          setActivityLogs(data.documents || []);
          setLastFetch(new Date());

          setCacheInfo({
            cached: data.cached || false,
            stale: data.stale || false,
            cacheAge: data.cacheAge || 0,
          });
        } else {
          throw new Error(data.error || "Failed to fetch logs");
        }
      } catch (err) {
        console.error("❌ Error fetching logs:", err.message);

        if (err.name === "AbortError" || err.message.includes("timeout")) {
          setLogsError("Request timed out. Please try again.");
        } else if (err.message.includes("fetch")) {
          setLogsError("Network error. Please check your connection.");
        } else {
          setLogsError(err.message);
        }
      } finally {
        setLogsLoading(false);
        fetchingRef.current = false;
      }
    },
    [lastFetch]
  );

  // Initial fetch on mount
  useEffect(() => {
    if (!isOnHomepage) {
      return;
    }

    const initFetch = async () => {
      await Promise.all([fetchLogs(true), fetchAnnouncements(true)]);
    };

    initFetch();
  }, [isOnHomepage, location.pathname]);

  // Manual refresh functions
  const handleRefreshLogs = () => {
    fetchLogs(true);
  };

  const handleRefreshAnnouncements = () => {
    fetchAnnouncements(true);
  };

  // Animation setup
  useEffect(() => {
    setIsLoaded(false);
    const timer = setTimeout(() => {
      setIsLoaded(true);
    }, 50);
    return () => clearTimeout(timer);
  }, [location.pathname]);

  // Handle scroll events to reposition tooltip
  useEffect(() => {
    const handleScroll = () => {
      if (hoveredTool) {
        setHoveredTool(null); // Hide tooltip on scroll
      }
    };

    window.addEventListener("scroll", handleScroll, true);
    return () => window.removeEventListener("scroll", handleScroll, true);
  }, [hoveredTool]);

  // Hero carousel functions
  const getIconComponent = (iconName) => {
    const iconMap = {
      Star: Star,
      Bot: Bot,
      Zap: Zap,
      Info: Info,
      AlertTriangle: AlertTriangle,
      Clock: Clock,
      Calculator: Calculator,
      Headphones: Headphones,
      Settings: Settings,
    };

    const IconComponent = iconMap[iconName] || Info;
    return <IconComponent className="w-8 h-8" />;
  };

 const getHeroSlides = () => {
  const defaultSlides = [
    {
      title: `${getGreeting()}, ${user?.name || "User"}!`,
      subtitle:
        "Welcome to your dashboard. Quickly access your tools and recent activity.",
      // Softer brand yellow gradient so the image side is clear
   gradient:
  "from-uasYellow/40 via-uasYellow/20 to-transparent " +
  "dark:from-transparent dark:via-transparent dark:to-transparent",

      cta: "Get Started",
      ctaLink: getQuickAccessCards()[0]?.path || "/home",
      icon: <Star className="w-8 h-8" />,
      type: "welcome",
      image: "/images/dashboard.png",
      imageAlt: "Dashboard Overview",
      // Optional: extra styling hints for the image itself
      imageClass:
        "rounded-2xl w-full h-64 object-cover shadow-xl ring-1 ring-white/50 dark:ring-white/10 filter contrast-110",
    },
  ];

    const announcementSlides = announcements.map((announcement) => {
const getPriorityConfig = (priority) => {
  switch (priority) {
    case 1: // CRITICAL → black
      return {
        gradient:
          "from-black/90 to-gray-900/80 dark:from-black/90 dark:to-gray-800/80",
        accent: "bg-black",
        textAccent: "text-white",
        badge: "bg-black/80 text-white",
        emoji: "🚨",
        label: "CRITICAL",
      };

    case 2: // HIGH → brand yellow
      return {
        gradient:
          "from-uasYellow/90 to-uasYellow/70 dark:from-uasYellow/90 dark:to-uasYellow/70",
        accent: "bg-uasYellow",
        textAccent: "text-uasYellow",
        badge: "bg-uasYellow/90 text-black",
        emoji: "⚠️",
        label: "HIGH PRIORITY",
      };

    case 3: // NORMAL → silver / medium gray
      return {
        gradient:
          "from-gray-400/70 to-gray-500/60 dark:from-gray-500/70 dark:to-gray-600/60",
        accent: "bg-gray-500",
        textAccent: "text-gray-800 dark:text-gray-200",
        badge: "bg-gray-500/80 text-white",
        emoji: "📢",
        label: "NORMAL",
      };

    case 4: // LOW → light gray
      return {
        gradient:
          "from-gray-200/60 to-gray-300/50 dark:from-gray-400/60 dark:to-gray-500/50",
        accent: "bg-gray-300",
        textAccent: "text-gray-600 dark:text-gray-200",
        badge: "bg-gray-300/80 text-black dark:text-white",
        emoji: "💡",
        label: "LOW PRIORITY",
      };

    case 5: // INFO → silver / soft neutral
      return {
        gradient:
          "from-gray-100/50 to-gray-200/40 dark:from-gray-300/50 dark:to-gray-400/40",
        accent: "bg-gray-200",
        textAccent: "text-gray-700 dark:text-gray-100",
        badge: "bg-gray-200/70 text-black dark:text-white",
        emoji: "🎯",
        label: "INFO",
      };

    default: // fallback → NORMAL
      return {
        gradient:
          "from-gray-400/70 to-gray-500/60 dark:from-gray-500/70 dark:to-gray-600/60",
        accent: "bg-gray-500",
        textAccent: "text-gray-800 dark:text-gray-200",
        badge: "bg-gray-500/80 text-white",
        emoji: "📢",
        label: "NORMAL",
      };
  }
};


      const priorityConfig = getPriorityConfig(announcement.priority);

      return {
        title: announcement.title,
        subtitle: announcement.message,
        gradient: priorityConfig.gradient,
        accent: priorityConfig.accent,
        textAccent: priorityConfig.textAccent,
        badge: priorityConfig.badge,
        priorityEmoji: priorityConfig.emoji,
        priorityLabel: priorityConfig.label,
        cta: "Learn More",
        ctaLink: announcement.actionUrl || "/home",
        icon: getIconComponent(announcement.icon),
        type: "announcement",
        priority: announcement.priority || 3,
        announcementType: announcement.type,
        image: announcement.image || "/images/announcement-default.png",
        imageAlt: announcement.imageAlt || "Announcement Image",
        scheduledTime: announcement.scheduledTime,
        expiresAt: announcement.expiresAt,
        isScheduledForFuture:
          announcement.scheduledTime &&
          new Date(announcement.scheduledTime) > new Date(),
        isExpired:
          announcement.expiresAt &&
          new Date(announcement.expiresAt) < new Date(),
        announcement: announcement,
      };
    });

    announcementSlides.sort((a, b) => a.priority - b.priority);

    const hasHighPriorityAnnouncements = announcementSlides.some(
      (slide) => slide.priority <= 2
    );

    if (!hasHighPriorityAnnouncements) {
      defaultSlides.push({
        title: "Boost Your Productivity",
        subtitle:
          "Explore our comprehensive suite of tools designed for excellence",
        gradient:
          "from-green-500 to-green-600 dark:from-green-700 dark:to-green-800",
        cta: "View Tutorial",
        ctaLink: "/tutorial",
        icon: <Zap className="w-8 h-8" />,
        type: "feature",
        image:
          "https://images.unsplash.com/photo-1460925895917-afdab827c52f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80",
        imageAlt: "Productivity Tools",
      });
    }

    return [...defaultSlides, ...announcementSlides];
  };

  const heroSlides = getHeroSlides();

  // Carousel auto-advance
  useEffect(() => {
    if (isPaused || heroSlides.length <= 1) return;

    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 5000);

    return () => clearInterval(timer);
  }, [heroSlides.length, isPaused]);

  // Cache status functions
  const getCacheStatus = () => {
    if (!lastFetch) return { text: "Never fetched", color: "text-gray-500" };

    const now = Date.now();
    const timeDiff = now - lastFetch.getTime();
    const minutesAgo = Math.floor(timeDiff / (1000 * 60));

    if (minutesAgo < 1) return { text: "Just now", color: "text-green-500" };
    if (minutesAgo < 5)
      return {
        text: `${minutesAgo}m ago${cacheInfo.cached ? " (cached)" : ""}`,
        color: "text-green-500",
      };
    if (minutesAgo < 10)
      return {
        text: `${minutesAgo}m ago${cacheInfo.stale ? " (stale)" : ""}`,
        color: "text-uasYellow",
      };

    return {
      text: `${minutesAgo}m ago (old)`,
      color: "text-red-500",
    };
  };

  const getAnnouncementCacheStatus = () => {
    if (!lastAnnouncementFetch)
      return { text: "Never fetched", color: "text-gray-500" };

    const now = Date.now();
    const timeDiff = now - lastAnnouncementFetch.getTime();
    const minutesAgo = Math.floor(timeDiff / (1000 * 60));

    if (minutesAgo < 1) return { text: "Just now", color: "text-green-500" };
    if (minutesAgo < 60)
      return {
        text: `${minutesAgo}m ago${
          announcementCacheInfo.cached ? " (cached)" : ""
        }`,
        color: "text-green-500",
      };

    return {
      text: `${Math.floor(minutesAgo / 60)}h ago (stale)`,
      color: "text-uasYellow",
    };
  };

  // Greeting function
  function getGreeting() {
    const hour = new Date().getHours();
    if (hour < 12) return "Good Morning";
    if (hour < 18) return "Good Afternoon";
    return "Good Evening";
  }

  // Quick access cards function
  function getQuickAccessCards() {
    const role = user?.role;
    const cards = [];

    if (["manager", "supervisor", "agent"].includes(role)) {
      cards.push(
        {
          title: "Rebate Calculator",
          icon: <Calculator className="text-uasYellow text-xl" />,
          description:
            "Quickly calculate customer rebates for service interruptions.",
          path: "/rebate-calculator",
        },
        {
          title: "Agent Assist",
          icon: <Headphones className="text-uasYellow text-xl" />,
          description:
            "Access AI-powered assistance for customer inquiries.",
          path: "/agent-assist",
        },
        {
          title: "Gnosis",
          icon: <Timer className="text-uasYellow text-xl" />,
          description:
            "Track and manage service interruption timelines.",
          path: "/gnosis",
        }
      );
    }

    if (["manager", "supervisor"].includes(role)) {
      cards.push(
        {
          title: "Access Management",
          icon: <Users className="text-uasYellow text-xl" />,
          description: "Manage user roles and permissions.",
          path: "/access-management",
        },
        {
          title: "Rebate Viewer",
          icon: <FileText className="text-uasYellow text-xl" />,
          description: "View and analyze all processed rebates.",
          path: "/rebate-viewer",
        }
      );
    }

    if (["manager", "Pasig", "supervisor"].includes(role)) {
      cards.push({
        title: "MOM Tool",
        icon: <Mic className="text-uasYellow text-xl" />,
        description: "Transcribe meeting recordings automatically.",
        path: "/audio-transcriber",
      });
    }

    if (role === "manager") {
      cards.push({
        title: "Agent Assist GPT",
        icon: <Headphones className="text-uasYellow text-xl" />,
        description: "Advanced AI assistance powered by GPT-4o.",
        path: "/agent-assist-gpt4o",
      });
    }

    if (["manager", "supervisor", "agent", "Pasig"].includes(role)) {
      cards.push(
        {
          title: "HR Assistant",
          icon: <Bot className="text-uasYellow text-xl" />,
          description:
            "Get help with HR and payroll related questions.",
          path: "/hrpay",
        },
        {
          title: "Settings",
          icon: <Settings className="text-uasYellow text-xl" />,
          description: "Configure your preferences and account settings.",
          path: "/settings",
        },
        {
          title: "Individual Development Plan",
          icon: <Bot className="text-uasYellow text-xl" />,
          description: "Personal Tracking of your development",
          path: "/idp-monitoring",
        }
      );
    }

    return cards;
  }

  const [showAll, setShowAll] = useState(false);
  const [expandedSlides, setExpandedSlides] = useState({});
  const [weather, setWeather] = useState(null);
  const [loading, setLoading] = useState(false);
  const [city, setCity] = useState("Angeles,PH");

  const quickAccessCards = getQuickAccessCards();
  const cacheStatus = getCacheStatus();
  const announcementCacheStatus = getAnnouncementCacheStatus();

  const toggleSlideExpansion = (index) => {
    setExpandedSlides((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  // Weather fetch
  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();

    const fetchWeather = async () => {
      setLoading(true);
      try {
        const res = await fetch(
          `http://localhost:5000/api/weather?city=${city}`,
          {
            signal: controller.signal,
          }
        );
        const data = await res.json();

        if (isMounted && data.success) {
          setWeather(data);
        }
      } catch (error) {
        if (isMounted) {
          console.error("❌ Weather fetch failed:", error);
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchWeather();
    const interval = setInterval(fetchWeather, 3600000);

    return () => {
      isMounted = false;
      controller.abort();
      clearInterval(interval);
    };
  }, [city]);

  return (
    <div className="p-4 md:p-6 lg:p-8 font-montserrat bg-lightmode dark:bg-darkmode bg-cover bg-center">
      {/* Hero Carousel */}
      <div
        className={`relative mb-6 rounded-xl overflow-hidden shadow-lg transition-all duration-500 hover:shadow-xl ${isLoaded ? "opacity-100" : "opacity-0"}`}
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        {/* Announcement status indicator */}
        {isOnHomepage && (
          <div className="absolute top-4 right-4 z-10 flex items-center gap-2">
            <div className="bg-black/20 backdrop-blur-sm rounded-full px-3 py-1 flex items-center gap-2">
              <div
                className={`w-2 h-2 rounded-full ${
                  announcementCacheStatus.color.includes("green")
                    ? "bg-green-400"
                    : announcementCacheStatus.color.includes("uasYellow")
                    ? "bg-uasYellow"
                    : "bg-red-400"
                }`}
              ></div>
              <span className="text-white text-xs">
                📢 {announcementCacheStatus.text}
              </span>
              {announcementsLoading && (
                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
              )}
            </div>
          </div>
        )}

        <div className="relative min-h-[16rem] md:min-h-[20rem]">
          {heroSlides.map((slide, index) => (
            <div
              key={index}
              className={`absolute inset-0 w-full h-full transition-opacity duration-400 ${
                index === currentSlide
                  ? "opacity-100 z-10 pointer-events-auto"
                  : "opacity-0 z-0 pointer-events-none"
              }`}
            >
              <div
                className={`bg-gradient-to-r ${slide.gradient} h-full flex items-center overflow-y-auto max-h-[34rem]`}
              >
                <div className="container mx-auto px-8 grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                  {/* Left Column - Content */}
                  <div className="text-white">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="p-3 bg-white/20 rounded-lg">{slide.icon}</div>
                      <div>
                        <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold">
                          {slide.title}
                        </h2>
                        {slide.type === "announcement" && (
                          <div className="text-white/80 text-sm mt-1 space-y-1">
                            <div className="flex items-center gap-2">
                              <span>
                                📢 {slide.announcementType?.toUpperCase() || "ANNOUNCEMENT"}
                              </span>
                              {slide.priority <= 2 && (
                                <span className="text-black bg-uasYellow px-2 py-0.5 rounded-full">
                                  ⚡ HIGH PRIORITY
                                </span>
                              )}
                            </div>

                            <div className="flex flex-wrap items-center gap-3 text-xs">
                              {slide.isScheduledForFuture && (
                                <span className="bg-blue-500/30 px-2 py-1 rounded-full">
                                  🔮 Scheduled for Future
                                </span>
                              )}
                              {slide.isExpired && (
                                <span className="bg-red-500/30 px-2 py-1 rounded-full">
                                  ⏰ Expired
                                </span>
                              )}
                              {slide.scheduledTime && !slide.isScheduledForFuture && (
                                <span className="bg-green-500/30 px-2 py-1 rounded-full">
                                  ✅ Active
                                </span>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="text-base md:text-lg mb-6 text-white/90 overflow-y-auto max-h-[calc(100vh-200px)] pr-3">
                      {slide.type === "announcement" ? (
                        <>
                          <div
                            className={`whitespace-pre-wrap break-words text-sm md:text-base text-white/90 leading-relaxed ${
                              expandedSlides[index] ? "" : "line-clamp-3"
                            }`}
                          >
                            {slide.subtitle
                              .split(/(https?:\/\/[^\s<>"{}|\\^`[\]]+)/g)
                              .map((part, i) =>
                                /^https?:\/\//.test(part) ? (
                                  <a
                                    key={i}
                                    href={part}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="underline hover:text-white break-all font-medium bg-white/10 px-1 py-0.5 rounded"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    {part}
                                  </a>
                                ) : (
                                  <span key={i}>{part}</span>
                                )
                              )}
                          </div>

                          {slide.type === "announcement" && slide.subtitle && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleSlideExpansion(index);
                              }}
                              className="mt-2 text-xs text-black bg-uasYellow/90 hover:bg-uasYellow px-2 py-1 rounded"
                            >
                              {expandedSlides[index] ? "Show Less ▲" : "Show More ▼"}
                            </button>
                          )}
                        </>
                      ) : (
                        <p>{slide.subtitle}</p>
                      )}
                    </div>

                    {slide.type === "announcement" &&
                      slide.announcement?.actionUrl &&
                      !slide.subtitle?.includes(slide.announcement.actionUrl) && (
                        <div className="mb-4 p-3 bg-white/10 rounded-lg border border-white/20">
                          <div className="text-white/70 text-xs mb-1">
                            🔗 Quick Link:
                          </div>
                          <a
                            href={slide.announcement.actionUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-white hover:text-black bg-uasYellow/80 hover:bg-uasYellow px-2 py-0.5 rounded underline break-all text-sm font-medium"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {slide.announcement.actionUrl}
                          </a>
                        </div>
                      )}

                    <div className="flex gap-3">
                      <button
                        onClick={() => {
                          if (
                            slide.ctaLink?.startsWith("http://") ||
                            slide.ctaLink?.startsWith("https://")
                          ) {
                            window.open(slide.ctaLink, "_blank", "noopener,noreferrer");
                          } else {
                            navigate(slide.ctaLink);
                          }
                        }}
                        className="px-6 py-3 bg-white text-gray-800 font-semibold rounded-lg hover:bg-gray-100 transition-colors flex items-center gap-2"
                      >
                        {slide.cta}
                        <ArrowRight className="w-4 h-4" />
                      </button>
                      {slide.cta !== "View Tutorial" && (
                        <button
                          onClick={() => navigate("/tutorial")}
                          className="px-6 py-3 bg-white/20 text-white font-semibold rounded-lg hover:bg-white/30 transition-colors"
                        >
                          View Tutorial
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Right Column - Image */}
                  <div className="hidden lg:block">
                    <div className="relative">
                      <img
                        src={slide.image}
                        alt={slide.imageAlt}
                        className="relative rounded-2xl w-full h-64 object-cover shadow-2xl border border-white/20"
                        loading="lazy"
                      />
                      <div className="absolute bottom-4 left-4 right-4 bg-black/30 rounded-lg p-3">
                        <p className="text-white text-sm">
                          {slide.type === "welcome" &&
                            "🏠 Your central hub for all tools and activities"}
                          {slide.type === "announcement" &&
                            `📢 ${slide.priorityLabel}`}
                          {slide.type === "feature" &&
                            "✨ Enhanced capabilities at your fingertips"}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Navigation Dots */}
        <div className="absolute z-30 pointer-events-auto bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2">
          {heroSlides.map((slide, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`w-3 h-3 rounded-full transition-colors ${
                index === currentSlide ? "bg-white" : "bg-white/50"
              }`}
              title={slide.type === "announcement" ? `📢 ${slide.title}` : slide.title}
            />
          ))}
        </div>

        {/* Navigation Arrows */}
        <button
          onClick={() =>
            setCurrentSlide((prev) => (prev - 1 + heroSlides.length) % heroSlides.length)
          }
          className="absolute z-30 left-4 top-1/2 transform -translate-y-1/2 bg-black/20 hover:bg-black/40 text-white p-2 rounded-full transition-colors pointer-events-auto"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>

        <button
          onClick={() =>
            setCurrentSlide((prev) => (prev + 1) % heroSlides.length)
          }
          className="absolute z-30 right-4 top-1/2 transform -translate-y-1/2 bg-black/20 hover:bg-black/40 text-white p-2 rounded-full transition-colors pointer-events-auto"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      {/* Announcement Error Display (UAS brand) */}
      {announcementsError && isOnHomepage && (
        <div className="mb-4 p-3 bg-uasYellow/10 dark:bg-uasYellow/10 border border-uasYellow/30 rounded-md">
          <div className="flex justify-between items-center">
            <p className="text-sm text-uasYellow">
              📢 Unable to load announcements: {announcementsError}
            </p>
            <button
              onClick={handleRefreshAnnouncements}
              className="text-uasYellow hover:underline text-sm"
            >
              Retry
            </button>
          </div>
        </div>
      )}

      {/* OPERATIONS HUB */}
      <div
        className={`relative transition-all duration-700 w-full rounded-2xl overflow-hidden ${
          isLoaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
        }`}
      >
        {/* Neutral glass background */}
        <div className="bg-white/80 dark:bg-laryzeDarkGray/80 backdrop-blur-md rounded-2xl border border-white/20 dark:border-gray-700/20 shadow-lg">
          <div className="w-full px-4 sm:px-6 lg:px-8 py-6">
            {/* Header */}
            <div className="mb-6">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-xl bg-laryzeGray dark:bg-laryzeDarkGray border border-white/30 dark:border-gray-600">
                  <Rocket className="w-6 h-6 text-gray-700 dark:text-white" />
                </div>
                <div>
                  <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900 dark:text-white leading-tight">
                    Operations Hub
                  </h2>
                  <p className="text-xs md:text-sm text-gray-600 dark:text-gray-300">
                    Your AI-powered workspace • {allTools.length} tools available
                  </p>
                </div>
              </div>
            </div>

            {/* Search + controls */}
            <div className="mb-4">
              <div className="bg-white/60 dark:bg-laryzeDarkGray/60 backdrop-blur-md rounded-xl p-3 border border-white/20 dark:border-gray-700/20 shadow-sm w-full">
                <div className="flex flex-col xl:flex-row gap-3 items-center">
                  {/* Search */}
                  <div className="relative flex-1 group w-full">
                    <div className="relative bg-white/50 dark:bg-laryzeDarkGray/50 backdrop-blur-sm rounded-lg border border-white/30 dark:border-gray-700/30">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400 w-4 h-4" />
                      <input
                        type="text"
                        placeholder="Search tools, categories, or features..."
                        className="w-full pl-9 pr-8 py-2.5 bg-transparent text-gray-800 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none text-sm rounded-lg"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                      {searchTerm && (
                        <button
                          onClick={() => setSearchTerm("")}
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 text-sm"
                          aria-label="Clear search"
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  </div>

                  {/* View + sort */}
                  <div className="flex items-center gap-2">
                    <div className="flex bg-white/50 dark:bg-laryzeDarkGray/50 backdrop-blur-sm rounded-lg p-0.5 border border-white/30 dark:border-gray-700/30">
                      <button
                        onClick={() => setViewMode("grid")}
                        className={`p-1.5 rounded-md transition-all ${
                          viewMode === "grid"
                            ? "bg-white/70 dark:bg-laryzeDarkGray/50 shadow-sm text-gray-900 dark:text-white"
                            : "text-gray-600 dark:text-gray-400"
                        }`}
                        title="Grid view"
                      >
                        <Grid3X3 className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => setViewMode("list")}
                        className={`p-1.5 rounded-md transition-all ${
                          viewMode === "list"
                            ? "bg-white/70 dark:bg-laryzeDarkGray/50 shadow-sm text-gray-900 dark:text-white"
                            : "text-gray-600 dark:text-gray-400"
                        }`}
                        title="List view"
                      >
                        <List className="w-3.5 h-3.5" />
                      </button>
                    </div>

                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      className="px-3 py-2 bg-white/50 dark:bg-laryzeDarkGray/50 backdrop-blur-sm border border-white/30 dark:border-gray-700/30 rounded-lg text-xs font-medium focus:outline-none text-gray-800 dark:text-white"
                    >
                      <option value="popularity">Most Popular</option>
                      <option value="name">Name A-Z</option>
                      <option value="category">Category</option>
                      <option value="updated">Recently Updated</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* Category tabs */}
            <div className="mb-4">
              <div className="flex flex-wrap justify-start lg:justify-center gap-2 w-full overflow-x-auto">
                {categories.map((cat) => {
                  const isActive = activeTab === cat;
                  return (
                    <button
                      key={cat}
                      onClick={() => setActiveTab(cat)}
                      className={`relative px-3 py-1.5 rounded-lg text-xs font-semibold transition-all border ${
                        isActive
                          ? "bg-white dark:bg-laryzeDarkGray border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white shadow-sm"
                          : "bg-white/50 dark:bg-laryzeDarkGray/50 border-white/30 dark:border-gray-700/30 text-gray-700 dark:text-gray-300 hover:bg-white/70 dark:hover:bg-laryzeDarkGray/70"
                      }`}
                    >
                      <span className="mr-1">
                        {cat === "All" ? "🧭" : categoryColors[cat]?.icon ?? "📁"}
                      </span>
                      {cat}
                      <span
                        className={`ml-2 inline-flex items-center justify-center w-5 h-5 text-xs font-bold rounded-full ${
                          isActive
                            ? "bg-gray-900 text-white dark:bg-white dark:text-gray-900"
                            : "bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200"
                        }`}
                      >
                        {categoryStats[cat]}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Tools grid/list */}
            <div>
              {viewMode === "grid" ? (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-3">
                  {filteredTools.map((tool, idx) => {
                    const IconComponent = iconMap[tool.name] || Bot;
                    const categoryConfig = categoryColors[tool.category];
                    return (
                      <div
                        key={idx}
                        onClick={() => handleCardClick(tool.path)}
                        onMouseEnter={(e) => {
                          const rect = e.currentTarget.getBoundingClientRect();
                          const tooltipWidth = 320;
                          const tooltipHeight = 400;
                          const gap = 10;
                          let x = rect.right + gap;
                          let y = rect.top;
                          if (x + tooltipWidth > window.innerWidth)
                            x = rect.left - tooltipWidth - gap;
                          if (x < 0) x = gap;
                          y = rect.top + rect.height / 2 - tooltipHeight / 2;
                          if (y + tooltipHeight > window.innerHeight)
                            y = window.innerHeight - tooltipHeight - gap;
                          if (y < gap) y = gap;
                          setTooltipPosition({ x, y });
                          setHoveredTool(tool);
                        }}
                        onMouseLeave={() => setHoveredTool(null)}
                        className="group cursor-pointer"
                        style={{
                          animationDelay: `${idx * 30}ms`,
                          animation: isLoaded
                            ? "fadeInUp 0.5s ease-out forwards"
                            : "none",
                        }}
                      >
                        <div className="relative h-full">
                          <div className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 blur-lg bg-gradient-to-r from-gray-300/20 via-gray-200/20 to-gray-300/20 dark:from-gray-600/20 dark:via-gray-700/20 dark:to-gray-600/20" />
                          <div className="relative bg-white/70 dark:bg-laryzeDarkGray/70 backdrop-blur-xl rounded-xl p-4 border border-white/30 dark:border-gray-700/30 h-full transition-all duration-300 group-hover:scale-[1.01] group-hover:shadow-xl overflow-hidden">
                            <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
                            <div className="flex items-start gap-3 mt-8">
                              <div className="relative flex-shrink-0">
                                <div
                                  className={`w-12 h-12 rounded-xl bg-gradient-to-br ${categoryConfig.gradient} p-2.5 group-hover:scale-110 transition-transform duration-300 shadow-lg relative overflow-hidden`}
                                >
                                  <div className="absolute inset-0 bg-white/15 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                                  <IconComponent className="w-full h-full text-white relative z-10" />
                                </div>
                              </div>
                              <div className="flex-1 min-w-0 pr-8">
                                <h3 className="text-sm font-bold text-gray-900 dark:text-white group-hover:text-gray-900 dark:group-hover:text-white transition-colors duration-300 mb-1">
                                  {tool.name}
                                </h3>
                                <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed line-clamp-2 mb-2">
                                  {tool.description}
                                </p>
                                <div className="flex items-center gap-3 text-xs">
                                  <span className={`font-medium ${categoryConfig.text}`}>
                                    {tool.category}
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div className="absolute right-3 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-all duration-300">
                              <ArrowRight className="w-5 h-5 text-gray-500 dark:text-gray-300" />
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="space-y-2">
                  {filteredTools.map((tool, idx) => {
                    const IconComponent = iconMap[tool.name] || Bot;
                    const categoryConfig = categoryColors[tool.category];
                    return (
                      <div
                        key={idx}
                        onClick={() => handleCardClick(tool.path)}
                        className="group cursor-pointer bg-white/70 dark:bg-laryzeDarkGray/70 backdrop-blur-md rounded-xl p-3 border border-white/20 dark:border-gray-700/20 hover:shadow-lg transition-all duration-300 relative overflow-hidden"
                      >
                        <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
                        <div className="flex items-center gap-3 relative z-10">
                          <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${categoryConfig.gradient} p-2.5 flex-shrink-0`}>
                            <IconComponent className="w-full h-full text-white" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="text-base font-bold text-gray-900 dark:text-white group-hover:text-gray-900 dark:group-hover:text-white transition-colors truncate">
                                {tool.name}
                              </h3>
                              {tool.isNew && (
                                <span className="px-1.5 py-0.5 bg-green-600 text-white text-xs font-bold rounded-full">
                                  NEW
                                </span>
                              )}
                              {tool.isTrending && (
                                <span className="px-1.5 py-0.5 bg-uasYellow text-black text-xs font-bold rounded-full">
                                  HOT
                                </span>
                              )}
                            </div>
                            <p className="text-gray-600 dark:text-gray-300 text-xs mb-1.5 line-clamp-1">
                              {tool.description}
                            </p>
                            <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
                              <span className={`${categoryConfig.text} font-medium`}>
                                {tool.category}
                              </span>
                              <span>•</span>
                              <span>Popularity: {tool.popularity}%</span>
                              <span>•</span>
                              <span>Updated {tool.lastUpdated}</span>
                            </div>
                          </div>
                          <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300 transition-colors flex-shrink-0" />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Empty state */}
              {filteredTools.length === 0 && (
                <div className="text-center py-12">
                  <div className="inline-flex items-center justify-center w-20 h-20 bg-laryzeGray dark:bg-laryzeDarkGray rounded-full mb-4">
                    <Search className="w-10 h-10 text-gray-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                    No tools found
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 mb-4 text-sm">
                    Try adjusting your search terms or category filters
                  </p>
                  <button
                    onClick={() => {
                      setSearchTerm("");
                      setActiveTab("All");
                    }}
                    className="px-4 py-2 bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-lg text-sm font-medium hover:opacity-90 transition-all shadow-sm"
                  >
                    Clear Filters
                  </button>
                </div>
              )}
            </div>

            {/* Tooltip */}
            {hoveredTool && (
              <div
                className="fixed bg-white dark:bg-laryzeDarkGray rounded-lg shadow-2xl border border-gray-200 dark:border-gray-700 p-4 pointer-events-none transition-all duration-200 z-[9999]"
                style={{
                  left: `${tooltipPosition.x}px`,
                  top: `${tooltipPosition.y}px`,
                  width: "280px",
                  maxHeight: "350px",
                  overflowY: "auto",
                  opacity: hoveredTool ? 1 : 0,
                  transform: hoveredTool ? "scale(1)" : "scale(0.95)",
                }}
              >
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-5 h-5 text-gray-700 dark:text-gray-200">
                    {(() => {
                      const IconComponent = iconMap[hoveredTool.name] || Bot;
                      return <IconComponent className="w-5 h-5" />;
                    })()}
                  </div>
                  <h3 className="font-bold text-gray-900 dark:text-white text-base">
                    {hoveredTool.name}
                  </h3>
                </div>
                <div className="space-y-3 text-xs">
                  <div>
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-1 flex items-center gap-1">
                      <Users className="w-3 h-3" /> Who Uses This
                    </h4>
                    <p className="text-gray-700 dark:text-gray-300">
                      {hoveredTool.whoUses}
                    </p>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-1 flex items-center gap-1">
                      <BookOpen className="w-3 h-3" /> How to Use
                    </h4>
                    <p className="text-gray-700 dark:text-gray-300">
                      {hoveredTool.howToUse}
                    </p>
                  </div>
                  <div>
                    <h4 className="font-semibold text-red-600 dark:text-red-400 mb-1">
                      Common Issues
                    </h4>
                    <ul className="space-y-0.5">
                      {hoveredTool.commonErrors.map((error, i) => (
                        <li key={i} className="text-gray-700 dark:text-gray-300 text-xs">
                          {error}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-green-600 dark:text-green-400 mb-1">
                      Best Practices
                    </h4>
                    <ul className="space-y-0.5">
                      {hoveredTool.bestPractices.map((p, i) => (
                        <li
                          key={i}
                          className="text-gray-700 dark:text-gray-300 text-xs flex items-start gap-1"
                        >
                          <div className="w-1 h-1 bg-green-500 rounded-full mt-1 flex-shrink-0" />
                          {p}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* I.T FRIENDS */}
      <div className="bg-white/80 dark:bg-laryzeDarkGray/80 backdrop-blur-md rounded-lg shadow-lg border border-white/20 dark:border-gray-700/20 p-6 mt-8">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 mb-8">
          <div>
            <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight text-gray-900 dark:text-white">
              I.T FRIENDS
            </h2>
          </div>
        </div>

        {/* Characters */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-10 lg:gap-8">
          {[
            {
              name: "YUNI",
              img: "/images/mascots/UAS_Yuni.png",
              desc: [
                "The face of UAS, embodying the spirit and identity of the company.",
              ],
            },
            {
              name: "BOB",
              img: "/images/mascots/UAS_Bob.png",
              desc: [
                "Champion of “Best Of Breed” IT infrastructure and innovation.",
              ],
            },
            {
              name: "ATMOS",
              img: "/images/mascots/UAS_Atmos.png",
              desc: ["Your trusted partner for in-country cloud solutions."],
            },
            {
              name: "ZETA",
              img: "/images/mascots/UAS_Zeta.png",
              desc: ["Expert in delivering top-notch BPO and managed services."],
            },
            {
              name: "CICI",
              img: "/images/mascots/UAS_Cici.png",
              desc: ["The friendly connector for all things client-related."],
            },
          ].map((char, i) => (
            <motion.div
              key={char.name}
              className="flex flex-col items-center text-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: i * 0.15 }}
              whileHover={{ scale: 1.05 }}
            >
              <img
                src={char.img}
                alt={char.name}
                className="w-40 h-40 object-contain mb-4"
                draggable={false}
              />
              <h3 className="text-xl font-extrabold text-uasYellow">{char.name}</h3>
              <div className="mt-3 text-sm text-gray-700 dark:text-gray-300 space-y-3 max-w-[24ch]">
                {char.desc.map((line, idx) => (
                  <p key={idx}>{line}</p>
                ))}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Mobile reminder */}
        <p className="mt-8 md:hidden text-xs text-gray-600 dark:text-gray-300">
          <span className="font-semibold">Reminder:</span> Please consult with the Marketing department
          before using any of our characters in any media.
        </p>
      </div>

      {/* Floating Contact Us Button */}
      <div className="fixed bottom-6 right-6 z-50">
        <a
          href="mailto:research@uas.com.ph"
          className="flex items-center gap-2 px-4 py-2 bg-uasYellow text-black rounded-full shadow-lg hover:bg-uasYellow/90 transition-all"
          title="Contact Us"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="w-5 h-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8m0 0v8a2 2 0 01-2 2H5a2 2 0 01-2-2V8m18-2a2 2 0 00-2-2H5a2 2 0 00-2 2v0"
            />
          </svg>
          <span className="text-sm font-medium">CONTACT US</span>
        </a>
      </div>

      <style jsx>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes slideInUp {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .line-clamp-3 {
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
};

export default HomePage;
